import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollToTop from "@/components/base/ScrollToTop";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import {
  getArticlesByCategory,
  getCategoryBySlug,
  getPopularArticles,
} from "@/lib/content";

const ORIGIN = "https://sjlm888.cn";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const list = getArticlesByCategory(slug);
  const popular = getPopularArticles(5);

  const name = category?.name ?? "未找到分类";
  const desc =
    category?.desc ?? "没有找到这个瓜田分类，返回首页看看今天有什么瓜吧。";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${name} - 51吃瓜`,
      description: desc,
      url: `${ORIGIN}/category/${slug}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name, item: `${ORIGIN}/category/${slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${name}最新内容`,
      itemListElement: list.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${ORIGIN}/article/${item.id}`,
        name: item.title,
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={`${name} - 51吃瓜今日新鲜瓜`}
        description={`${desc} 51吃瓜${name}栏目每日更新今日新鲜瓜，实时同步最新动态。`}
        keywords={`51吃瓜,${name},51吃瓜今日新鲜瓜,51吃瓜每日新瓜,今日新鲜瓜`}
        path={`/category/${slug}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-8 bg-background-100">
          <div className="max-w-content mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "瓜田分类", path: `/category/${categories[0].slug}` },
                { label: name },
              ]}
            />
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
                  <span className="w-4 h-px bg-primary-500"></span>
                  Category
                </span>
                <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
                  {name}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {desc}
                </p>
              </div>
              {category && (
                <span className="inline-flex items-center gap-2 self-start md:self-auto px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 whitespace-nowrap">
                  <i className={`${category.emoji} text-primary-500`}></i>
                  共 {list.length} 条瓜
                </span>
              )}
            </div>

            <nav aria-label="瓜田分类切换" className="mt-6 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm border transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50 border-primary-500"
                      : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {list.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {list.map((item) => (
                    <ArticleCard key={item.id} article={item} />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-background-200 bg-background-100 p-10 text-center">
                  <i className="ri-emotion-happy-line text-4xl text-foreground-300"></i>
                  <p className="mt-4 text-foreground-700">
                    这个瓜田暂时还没有内容。
                  </p>
                  <Link
                    to="/"
                    className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    返回首页看新瓜
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-fire-fill text-primary-500"></i>
                  热门阅读
                </h2>
                <ul className="mt-4 divide-y divide-background-200">
                  {popular.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/article/${item.id}`}
                        className="group flex items-start gap-3 py-3 cursor-pointer"
                      >
                        <span
                          className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm text-foreground-800 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-secondary-900 text-secondary-50 p-5">
                <h2 className="font-heading font-bold text-lg">订阅新瓜早报</h2>
                <p className="mt-2 text-sm text-secondary-100/80 leading-relaxed">
                  每天早上八点，把新鲜瓜整理好发给你。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-notification-3-line"></i>
                  去订阅
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}